/* eslint-disable react-hooks/exhaustive-deps */
import * as queryString from 'query-string';
import Box from '@amzn/awsui-components-react/polaris/box';
import React, {useEffect, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {
    fetchAcDetailsById,
    fetchAcIdByKey,
    fetchIapByAcAndFnsku,
    fetchProgramById,
} from '../../backend/backend';
import {getAcPageUrl} from '../../backend/internalUrls';
import {AvailabilityConsumer} from '../../backend/model';
import {isValidAcId, isValidNumericId} from '../../validation/InputValidator';
import AcSearchPanel from '../ac/AcSearchPanel';
import AjaxStatusIndicator from '../basic/AjaxStatusIndicator';
import AcSummaryPanel from './AcSummaryPanel';
import {parseErrorMessage} from '../../utils/errors';

type AcPageProps = RouteComponentProps<{ acId?: string; marketplaceId?: string; merchantCustomerId?: string }>

const AcPage: React.FC<AcPageProps> = (props) => {
    const [ac, setAc] = useState<AvailabilityConsumer>();
    const [programDescription, setProgramDescription] = useState<string>();
    const [iapId, setIapId] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);

    const resetState = (): void => {
        setAc(undefined);
        setProgramDescription(undefined);
        setIapId(undefined);
        setErrorMessages([]);
    };

    const acIdFromUrl = (props.match.params.acId as string)?.trim() || '';
    const queries = queryString.parse(props.location.search) || '';
    const marketplaceIdFromUrl = (queries.marketplaceId as string)?.trim() || '';
    const merchantCustomerIdFromUrl = (queries.merchantCustomerId as string)?.trim() || '';
    const programIdFromUrl = (queries.programId as string)?.trim() || '';
    const regionIdFromUrl = (queries.regionId as string)?.trim() || '';
    const fnskuFromUrl = (queries.fnsku as string)?.trim() || '';

    useEffect(() => {
        searchAcDetails(
            acIdFromUrl,
            marketplaceIdFromUrl,
            merchantCustomerIdFromUrl,
            programIdFromUrl,
            regionIdFromUrl,
            fnskuFromUrl,
        );
    }, [acIdFromUrl, marketplaceIdFromUrl, merchantCustomerIdFromUrl, programIdFromUrl, regionIdFromUrl, fnskuFromUrl]);

    const searchAcDetails = async (
        acId: string, marketplaceId: string, merchantCustomerId: string, programId: string, regionId: string, fnsku: string,
    ) => {
        try {
            resetState();

            if (isValidAcId(acId) || (isValidNumericId(marketplaceId) && isValidNumericId(merchantCustomerId))) {
                setIsLoading(true);
            } else return;

            const acResp = acId ? await fetchAcDetailsByIdTask(acId) :
                await fetchAcIdByKeyTask(marketplaceId, merchantCustomerId, programId, regionId);
            setAc(acResp);

            try {
                const programDescription = await fetchProgramDescriptionByIdTask(acResp.key.fulfillmentProgramId);
                setProgramDescription(programDescription);
            } catch (e: any) {
                if (e.response?.status === 404) {
                    setProgramDescription('Program does not exist in PJS.');
                } else {
                    setProgramDescription('Failed to fetch program description.');
                }
            }

            try {
                const iapResp = await fetchIapByAcAndFnsku(acResp.id, fnsku);
                setIapId(iapResp.data);
            } catch (e: any) {
                if (!(e.response.data['type'] === 'NoSuchElementException')) {
                    throw e;
                }
            }
        } catch (error: any) {
            console.log(error);
            setErrorMessages([parseErrorMessage(error)]);
            setAc(undefined);
        } finally {
            setIsLoading(false);
        }
    };

    async function fetchAcDetailsByIdTask(acId: string): Promise<AvailabilityConsumer> {
        const ac = await fetchAcDetailsById(acId);
        return {
            id: acId,
            key: ac.data,
        };
    }

    async function fetchAcIdByKeyTask(
        marketplaceId: string, merchantCustomerId: string, programId: string, regionId: string,
    ): Promise<AvailabilityConsumer> {
        const acId = await fetchAcIdByKey(marketplaceId, merchantCustomerId, programId, regionId);
        return {
            id: acId.data,
            key: {
                marketplaceId: marketplaceId,
                merchantCustomerId: merchantCustomerId,
                fulfillmentProgramId: programId,
                fulfillmentRegionId: regionId,
            },
        };
    }

    async function fetchProgramDescriptionByIdTask(programId: string): Promise<string> {
        const programResponse = await fetchProgramById(programId);
        return programResponse.data.description;
    }

    return (
        <div>
            <Box variant="h1">Availability Consumer (AC)</Box>
            <AcSearchPanel
                acId={acIdFromUrl}
                marketplaceId={marketplaceIdFromUrl}
                merchantCustomerId={merchantCustomerIdFromUrl}
                programId={programIdFromUrl}
                regionId={regionIdFromUrl}
                fnsku={fnskuFromUrl}
                searchHandler={
                    (acId: string, marketplaceId: string, merchantCustomerId: string, programId: string, regionId: string, fnsku: string) =>
                        props.history.push(getAcPageUrl(acId, marketplaceId, merchantCustomerId, programId, regionId, fnsku))
                }
            />
            <br/>
            <AjaxStatusIndicator errorMessage={errorMessages.join(' ')} loading={isLoading}/>
            <br/>
            {
                ac &&
                <AcSummaryPanel ac={ac} iapId={iapId} fnsku={fnskuFromUrl} programDescription={programDescription}/>
            }
        </div>
    );
};

export default AcPage;
